import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeTemplate, MEDIA } from '@cloudsign/shared';
import type { BadgeLayout } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Badge, Button, Card, EmptyState, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useAllSites, useTemplates } from './api';
import type { StatusFilter } from './api';

const MEDIA_KEYS = Object.keys(MEDIA) as BadgeLayout['media'][];

function NewDesign({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sites = useAllSites();
  const [name, setName] = useState('');
  const [media, setMedia] = useState<BadgeLayout['media']>('dk11202_62x100');
  const [siteId, setSiteId] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/badges/templates', {
        body: {
          name: name.trim(),
          siteId: siteId === '' ? null : siteId,
          layout: {
            media,
            orientation: 'landscape',
            elements: [
              {
                id: 'el-1',
                type: 'text',
                x: 2,
                y: 2,
                w: 40,
                h: 10,
                source: { kind: 'data', key: 'visitor_name' },
                fontSize: 20,
                bold: true,
                align: 'left',
                shrinkToFit: true,
                maxLines: 1,
              },
            ],
          },
        },
        schema: BadgeTemplate,
      }),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['badges', 'templates'] });
      void navigate(`/manage/badges/${created.id}`);
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t('badgeUi.create.title')}>
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
        <TextField label={t('badgeUi.field.name')} value={name} maxLength={80} required error={errs['name'] ? t(errs['name']) : undefined} onChange={(e) => setName(e.target.value)} />
        <SelectField label={t('badgeUi.field.media')} value={media} onChange={(e) => setMedia(e.target.value as BadgeLayout['media'])}>
          {MEDIA_KEYS.map((m) => (
            <option key={m} value={m}>
              {t(`badgeUi.media.${m}`)}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('badgeUi.field.site')} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">{t('badgeUi.site.all')}</option>
          {(sites.data?.items ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <div className="flex gap-2 sm:col-span-3">
          <Button type="submit" variant="primary" disabled={name.trim() === '' || create.isPending}>
            {t('badgeUi.create.submit')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
        {create.error && Object.keys(errs).length === 0 ? <ErrorLine error={create.error} /> : null}
      </form>
    </Card>
  );
}

export function TemplatesPage() {
  const qc = useQueryClient();
  const canManage = useCan('badges.manage');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [creating, setCreating] = useState(false);
  const list = useTemplates(status);
  const sites = useAllSites();
  const siteName = (id: string | null) => (id === null ? t('badgeUi.site.all') : (sites.data?.items.find((s) => s.id === id)?.name ?? ''));
  const setArchived = useMutation({
    mutationFn: (v: { id: string; status: 'active' | 'archived' }) => api.patch(`/badges/templates/${v.id}`, { body: { status: v.status }, schema: BadgeTemplate }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['badges', 'templates'] }),
  });

  return (
    <div>
      <PageHeader
        title={t('badgeUi.title')}
        description={t('badgeUi.description')}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('badgeUi.new')}
            </Button>
          ) : null
        }
      />
      {creating && canManage ? (
        <div className="mb-6">
          <NewDesign onDone={() => setCreating(false)} />
        </div>
      ) : null}
      <div className="mb-4 max-w-xs">
        <SelectField label={t('common.status')} value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
          <option value="active">{t('badgeUi.filter.active')}</option>
          <option value="archived">{t('badgeUi.filter.archived')}</option>
          <option value="all">{t('badgeUi.filter.all')}</option>
        </SelectField>
      </div>
      {setArchived.error ? <ErrorLine error={setArchived.error} /> : null}
      {list.isError ? (
        <ErrorPanel error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Spinner />
      ) : (
        <TableWrap caption={t('badgeUi.title')}>
          <thead>
            <tr>
              <Th>{t('badgeUi.col.name')}</Th>
              <Th>{t('badgeUi.col.media')}</Th>
              <Th>{t('badgeUi.col.site')}</Th>
              <Th>{t('badgeUi.col.version')}</Th>
              <Th>{t('badgeUi.col.status')}</Th>
              {canManage ? <Th>{t('common.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {list.data.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 6 : 5}>
                  <EmptyState message={t('badgeUi.empty')} />
                </td>
              </tr>
            ) : null}
            {list.data.map((d) => (
              <tr key={d.id} className="border-t border-slate-200">
                <td className="px-3 py-2">
                  <Link to={`/manage/badges/${d.id}`} className="font-medium text-brand underline">
                    {d.name}
                  </Link>
                </td>
                <td className="px-3 py-2">{t(`badgeUi.media.${d.layout.media}`)}</td>
                <td className="px-3 py-2">{siteName(d.siteId)}</td>
                <td className="px-3 py-2">{t('badgeUi.version', { version: d.version })}</td>
                <td className="px-3 py-2">
                  <Badge tone={d.status === 'active' ? 'good' : 'neutral'}>{t(`status.${d.status}`)}</Badge>
                </td>
                {canManage ? (
                  <td className="px-3 py-2">
                    <Button disabled={setArchived.isPending} onClick={() => setArchived.mutate({ id: d.id, status: d.status === 'active' ? 'archived' : 'active' })}>
                      {d.status === 'active' ? t('badgeUi.archive') : t('badgeUi.restore')}
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
